import axios, { AxiosInstance } from "axios";
import logger from "../utils/logger";
import { ApiResponse } from "../types";
import { Constants } from "../utils/constants";
import billingService from "./billingService";

// Types for Payment API
export interface Bank {
  id: number;
  name: string;
  code: string;
  country: string;
}

export interface AccountType {
  id: number;
  name: string;
  code: string;
  description?: string;
}

export interface BusinessCategory {
  id: number;
  name: string;
  code: string;
  description?: string;
}

// Input payload from form/UI
export interface PaymentSetupPayload {
  paymentMethod?: "bank" | "paybill" | "till"; // Optional - determined from payload fields
  userId?: number;

  // Payment institution fields
  paymentInstitutionId?: string | number;
  paymentInstitutionCode?: string; // Payment institution code (required by API)
  accountType?: string;
  accountNumber?: string;
  accountName?: string;
  branchCode?: string;
  swiftCode?: string;

  // M-Pesa Paybill specific fields
  paybillNumber?: string;
  paybillShortcode?: string;
  businessRegistrationNumber?: string;
  businessLicenseNumber?: string;
  businessCategory?: string;
  taxPinNumber?: string;
  businessOperatingHours?: string;

  // M-Pesa Till specific fields
  tillNumber?: string;
  tillName?: string;

  // Shared fields for both M-Pesa methods and business info
  businessName?: string;
  businessPhoneNumber?: string;
  businessEmailAddress?: string;
  contactPersonName?: string;
  contactPersonPhone?: string;
  contactPersonEmail?: string;
  contactPersonIdNumber?: string;
  currency?: string;
  primary?: boolean; // Hardcoded to true in form
  paymentInstitutionType?: string;
}

// API request payload for POST /api/v1/organizations/{organizationId}/payment-setup
export interface PaymentSetupApiPayload {
  paymentInstitutionCode: string;
  paymentInstitutionType?: string;
  businessName: string;
  accountNumber: string;
  description: string;
  primaryContactEmail: string;
  primaryContactName: string;
  primaryContactPhone: string;
  currency: string;
  primary: boolean;
}

export interface PaymentMethod {
  id: number;
  userId: number;
  paymentMethod: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt?: string;
  details: any;
}

export interface PaymentInstitutionType {
  id: number;
  name: string;
  code: string;
  countryCode?: string;
  countryId?: number;
  description?: string;
  minAccountNumberLength?: number;
  maxAccountNumberLength?: number;
  createdAt: string;
  modifiedAt?: string;
}

class PaymentService {
  private paymentApi: AxiosInstance;

  constructor() {
    // Create payment service API instance
    this.paymentApi = axios.create({
      baseURL: Constants.PAYMENT_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth interceptor for payment API
    this.paymentApi.interceptors.request.use(config => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        const hasBearer = token.toLowerCase().startsWith("bearer ");
        config.headers.Authorization = hasBearer ? token : `Bearer ${token}`;
      }

      // Add client key for payment setup endpoints (required for POST/PUT operations)
      if (config.url?.includes("/payment-setup")) {
        config.headers["Client-Key"] = "OwH54uHJiRpumtszP2iiTeoRXxCol08RBxS6qY9Q";
      }

      return config;
    });

    // Add response interceptor
    this.paymentApi.interceptors.response.use(
      response => ({
        data: response.data,
        status: response.status,
        message: response.data?.message,
      }),
      error => {
        logger.error("PaymentService API Error:", error);
        throw error;
      }
    );
  }

  public async getPaymentInstitutions(currency?: string, country?: string): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting payment institutions list for currency:", currency, "and country:", country);
      const params: any = {};
      if (currency) {
        params.currency = currency;
      }
      if (country) {
        params.country = country;
      }
      const response = await this.paymentApi.get("/api/v1/miscellaneous/payment-institutions", { params });
      logger.log("Get payment institutions response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment institutions:", error);
      throw error;
    }
  }

  public async getCountries(): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting countries list");
      const response = await this.paymentApi.get("/api/v1/miscellaneous/countries");
      logger.log("Get countries response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting countries:", error);
      throw error;
    }
  }

  public async getPaymentChannels(): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting payment channels list");
      const response = await billingService.getPaymentChannels();
      logger.log("Get payment channels response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment channels:", error);
      throw error;
    }
  }

  public async getPaymentInstitutionBranches(): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting payment institution branches list");
      const response = await billingService.getPaymentInstitutionBranches();
      logger.log("Get payment institution branches response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment institution branches:", error);
      throw error;
    }
  }

  public async getPaymentClients(): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting payment clients list");
      const response = await this.paymentApi.get("/api/v1/payment-clients");
      logger.log("Get payment clients response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment clients:", error);
      throw error;
    }
  }

  public async getPaymentProviders(): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting payment providers list");
      const response = await this.paymentApi.get("/api/v1/payment-providers");
      logger.log("Get payment providers response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment providers:", error);
      throw error;
    }
  }

  public async getOrganizationPaymentSetups(organizationId: number): Promise<ApiResponse<any[]>> {
    try {
      logger.log("Getting organization payment setups for organizationId:", organizationId);
      const response = await this.paymentApi.get(`/api/v1/organizations/${organizationId}/payment-setup`);
      logger.log("Get organization payment setups response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting organization payment setups:", error);
      throw error;
    }
  }

  public async getAccountTypes(): Promise<ApiResponse<AccountType[]>> {
    try {
      logger.log("Getting account types");
      const response = await this.paymentApi.get("/api/v1/account-types");
      logger.log("Get account types response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting account types:", error);
      throw error;
    }
  }

  public async getAccountTypeById(id: number): Promise<ApiResponse<AccountType>> {
    try {
      logger.log("Getting account type by id:", id);
      const response = await this.paymentApi.get(`/api/v1/account-types/${id}`);
      logger.log("Get account type by id response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting account type by id:", error);
      throw error;
    }
  }

  public async getBusinessCategories(): Promise<ApiResponse<BusinessCategory[]>> {
    try {
      logger.log("Getting business categories");
      const response = await this.paymentApi.get("/api/v1/business-categories");
      logger.log("Get business categories response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting business categories:", error);
      throw error;
    }
  }

  /**
   * Submit payment setup for an organization
   * API: POST /api/v1/organizations/{organizationId}/payment-setup
   *
   * Request body structure:
   * {
   *   "paymentInstitutionCode": string,
   *   "businessName": string,
   *   "accountNumber": string,
   *   "description": string,
   *   "primaryContactEmail": string,
   *   "primaryContactName": string,
   *   "primaryContactPhone": string,
   *   "primary": boolean
   * }
   */
  public async submitPaymentSetup(
    payload: PaymentSetupPayload | PaymentSetupApiPayload,
    organizationId: number
  ): Promise<ApiResponse<PaymentMethod>> {
    try {
      logger.log("Submitting payment setup with payload:", payload);

      // Check if payload already matches PaymentSetupApiPayload structure
      const isApiPayload = (p: any): p is PaymentSetupApiPayload => {
        return (
          p.paymentInstitutionCode !== undefined &&
          p.businessName !== undefined &&
          p.accountNumber !== undefined &&
          p.primaryContactEmail !== undefined
        );
      };

      let transformedPayload: PaymentSetupApiPayload;

      if (isApiPayload(payload)) {
        // Payload already in API format, use it directly (but ensure currency is included and is a string)
        if (!payload.currency || typeof payload.currency !== "string" || payload.currency.trim() === "") {
          throw new Error("Currency is required and must be a 3-letter currency code (string)");
        }
        transformedPayload = {
          ...payload,
          currency: payload.currency.trim(), // Ensure it's a trimmed string (3-letter code)
        };
      } else {
        // Transform the payload to match the API structure
        // Determine account number from payload fields (paybill, till, or bank account)
        let accountNumber: string;

        if (payload.paybillNumber && payload.paybillNumber.trim() !== "") {
          accountNumber = payload.paybillNumber || payload.paybillShortcode || "";
        } else if (payload.tillNumber && payload.tillNumber.trim() !== "") {
          accountNumber = payload.tillNumber || "";
        } else {
          accountNumber = payload.accountNumber || "";
        }

        // Validate currency is provided (required field)
        const currencyCode = payload.currency;
        if (!currencyCode || typeof currencyCode !== "string" || currencyCode.trim() === "") {
          throw new Error("Currency is required and must be a 3-letter currency code (string)");
        }

          transformedPayload = {
          paymentInstitutionCode: payload.paymentInstitutionCode || "",
          paymentInstitutionType: payload.paymentInstitutionType,
          businessName: payload.accountName || payload.businessName || "Business Name",
          accountNumber: accountNumber,
          description: "Payment setup",
          primaryContactEmail: payload.businessEmailAddress || payload.contactPersonEmail || "contact@example.com",
          primaryContactName: payload.contactPersonName || payload.businessName || "Contact Person",
          primaryContactPhone: payload.businessPhoneNumber || payload.contactPersonPhone || "+254700000000",
          currency: currencyCode.trim(), // Pass 3-letter currency code as string (required)
          primary: payload.primary !== undefined ? payload.primary : true,
        };
      }

      logger.log("Debug - final transformed payload:", transformedPayload);

      const response = await this.paymentApi.post(
        `/api/v1/organizations/${organizationId}/payment-setup`,
        transformedPayload
      );
      logger.log("Submit payment setup response:", response);
      return response;
    } catch (error) {
      logger.error("Error submitting payment setup:", error);
      throw error;
    }
  }

  public async createAccountType(payload: {
    name: string;
    code: string;
    description?: string;
  }): Promise<ApiResponse<AccountType>> {
    try {
      logger.log("Creating account type with payload:", payload);
      const response = await this.paymentApi.post("/api/v1/account-types", payload);
      logger.log("Create account type response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating account type:", error);
      throw error;
    }
  }

  public async createBusinessCategory(payload: {
    name: string;
    code: string;
    description?: string;
  }): Promise<ApiResponse<BusinessCategory>> {
    try {
      logger.log("Creating business category with payload:", payload);
      const response = await this.paymentApi.post("/api/v1/business-categories", payload);
      logger.log("Create business category response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating business category:", error);
      throw error;
    }
  }

  public async createPaymentClient(payload: {
    name: string;
    code: string;
    active?: boolean;
    callbackMode?: string;
    callbackAddress?: string;
  }): Promise<ApiResponse<any>> {
    try {
      logger.log("Creating payment client with payload:", payload);
      const response = await this.paymentApi.post("/api/v1/payment-clients", payload);
      logger.log("Create payment client response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating payment client:", error);
      throw error;
    }
  }

  public async createPaymentProvider(payload: {
    code: string;
    name: string;
    priority?: number;
    active?: boolean;
    channelIds?: number[];
  }): Promise<ApiResponse<any>> {
    try {
      logger.log("Creating payment provider with payload:", payload);
      const response = await this.paymentApi.post("/api/v1/payment-providers", payload);
      logger.log("Create payment provider response:", response);
      return response;
    } catch (error) {
      logger.error("Error creating payment provider:", error);
      throw error;
    }
  }

  public async getPaymentSetup(organizationId: number, paymentSetupId: number): Promise<ApiResponse<any>> {
    try {
      logger.log("Getting payment setup with ID:", paymentSetupId, "for organization:", organizationId);
      const response = await this.paymentApi.get(
        `/api/v1/organizations/${organizationId}/payment-setup/${paymentSetupId}`
      );
      logger.log("Get payment setup response:", response);
      return response;
    } catch (error) {
      logger.error("Error getting payment setup:", error);
      throw error;
    }
  }

  public async updatePaymentSetup(
    organizationId: number,
    paymentSetupId: number,
    payload: any
  ): Promise<ApiResponse<any>> {
    try {
      logger.log("Updating payment setup with ID:", paymentSetupId, "for organization:", organizationId);
      logger.log("Update payload:", payload);
      // Use PATCH method for updates as per API specification
      const response = await this.paymentApi.patch(
        `/api/v1/organizations/${organizationId}/payment-setup/${paymentSetupId}`,
        payload
      );
      logger.log("Update payment setup response:", response);
      return response;
    } catch (error) {
      logger.error("Error updating payment setup:", error);
      throw error;
    }
  }

  public async deletePaymentSetup(organizationId: number, paymentSetupId: number): Promise<ApiResponse<void>> {
    try {
      logger.log("Deleting payment setup with ID:", paymentSetupId, "for organization:", organizationId);
      const response = await this.paymentApi.delete(
        `/api/v1/organizations/${organizationId}/payment-setup/${paymentSetupId}`
      );
      logger.log("Delete payment setup response:", response);
      return response;
    } catch (error) {
      logger.error("Error deleting payment setup:", error);
      throw error;
    }
  }

  // ========================================
  // PAYMENT INSTITUTION TYPES
  // ========================================
  public async getPaymentInstitutionTypes(params?: {
    name?: string;
    code?: string;
    countryCode?: string;
    page?: number;
    limit?: number;
    orderBy?: string;
  }): Promise<ApiResponse<PaymentInstitutionType[]>> {
    try {
      const response = await this.paymentApi.get("/api/v1/payment-institution-types", { params });
      const rawData = response.data?.data !== undefined ? response.data.data : response.data;
      const typesList = Array.isArray(rawData) ? rawData : [];
      return {
        data: typesList,
        status: response.status,
        message: response.data?.message || "Payment institution types retrieved successfully",
      };
    } catch (error) {
      logger.error("Error getting payment institution types:", error);
      throw error;
    }
  }

  public async getPaymentInstitutionTypeById(id: number): Promise<ApiResponse<PaymentInstitutionType>> {
    try {
      const response = await this.paymentApi.get(`/api/v1/payment-institution-types/${id}`);
      return response;
    } catch (error) {
      logger.error("Error getting payment institution type by id:", error);
      throw error;
    }
  }

  public async createPaymentInstitutionType(payload: {
    name: string;
    code: string;
    countryCode?: string;
    description?: string;
  }): Promise<ApiResponse<PaymentInstitutionType>> {
    try {
      const response = await this.paymentApi.post("/api/v1/payment-institution-types", payload);
      return response;
    } catch (error) {
      logger.error("Error creating payment institution type:", error);
      throw error;
    }
  }

  public async updatePaymentInstitutionType(
    id: number,
    payload: {
      name?: string;
      code?: string;
      countryCode?: string;
      description?: string;
    }
  ): Promise<ApiResponse<PaymentInstitutionType>> {
    try {
      const response = await this.paymentApi.patch(`/api/v1/payment-institution-types/${id}`, payload);
      return response;
    } catch (error) {
      logger.error("Error updating payment institution type:", error);
      throw error;
    }
  }

  public async deletePaymentInstitutionType(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.paymentApi.delete(`/api/v1/payment-institution-types/${id}`);
      return response;
    } catch (error) {
      logger.error("Error deleting payment institution type:", error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService();
export default paymentService;
